"use strict";
/**
 * Shared locale utilities used by both client and server for ISO locale
 * handling.
 *
 * Defines the canonical `IsoLocale` type and the supported locale set. PSI used
 * to carry a parallel "legacy key" format (e.g. `'french_swiss'`) — those types
 * and the conversion helpers have been deleted. The only remaining tolerance
 * for legacy strings lives in `legacy-language-adapter.ts`, which is the
 * deprecated boundary adapter for the two surfaces (`language_g` URL param and
 * `admin_language` user global) where partner-stored values may still arrive in
 * the old format.
 *
 * This file must not import Node-only or browser-only modules — it is consumed
 * by both the React/Expo client and the Firebase Functions server.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_ISO_LOCALE = exports.ALL_ISO_LOCALES = void 0;
exports.setLocaleErrorReporter = setLocaleErrorReporter;
exports.isSwissLocale = isSwissLocale;
exports.isKnownIsoLocale = isKnownIsoLocale;
exports.parseIsoLocale = parseIsoLocale;
exports.expandPrimarySubtag = expandPrimarySubtag;
exports.localeToLanguageCode = localeToLanguageCode;
exports.ALL_ISO_LOCALES = ['en-US', 'fr-FR', 'fr-CA', 'fr-CH', 'fr-BE', 'de-DE', 'de-CH', 'it-IT', 'it-CH', 'rm-CH', 'nl-NL'];
const KNOWN_ISO_SET = new Set(exports.ALL_ISO_LOCALES);
exports.DEFAULT_ISO_LOCALE = 'en-US';
/**
 * Pluggable error reporter. Default is console.warn. Server and client wire
 * Sentry into this on app startup. Lives here (not directly imported from a
 * Sentry SDK) because packages/shared must remain runtime-agnostic.
 */
let reportUnknownLocale = (s) => {
    if (typeof console !== 'undefined') {
        console.warn(`[locale] unknown locale string: "${s}"`);
    }
};
function setLocaleErrorReporter(fn) {
    reportUnknownLocale = fn;
}
/**
 * Swiss linguistic variants — UI helpers prefix these locales with the Swiss
 * cross.
 */
function isSwissLocale(locale) {
    return locale.endsWith('-CH');
}
/** Type predicate narrowing a string to IsoLocale. No side effects. */
function isKnownIsoLocale(s) {
    return KNOWN_ISO_SET.has(s);
}
/**
 * Parse an unknown string into an IsoLocale.
 * - Known ISO → narrowed value.
 * - null/undefined → null silently (caller is responsible for handling
 *   absence).
 * - Any other string (including empty string) → null AND reports via the
 *   configured reporter. A non-null, non-ISO string almost always indicates a
 *   partner integration sending a stale or malformed locale value at a surface
 *   that no longer accepts them.
 */
function parseIsoLocale(s) {
    if (s == null)
        return null;
    if (isKnownIsoLocale(s))
        return s;
    reportUnknownLocale(s);
    return null;
}
/**
 * Default ISO locale for each BCP 47 primary subtag. Used so that a partner
 * sending just `'de'` (no region) resolves to `'de-DE'` rather than falling
 * back to the global default. Hand-curated rather than derived from
 * ALL_ISO_LOCALES because the "primary regional default" for a subtag is
 * editorial — e.g. `'fr'` should resolve to `'fr-FR'` not `'fr-BE'`.
 */
const PRIMARY_SUBTAG_DEFAULT = {
    en: 'en-US',
    fr: 'fr-FR',
    de: 'de-DE',
    it: 'it-IT',
    rm: 'rm-CH',
    nl: 'nl-NL',
};
/**
 * Resolve a bare BCP 47 primary subtag (e.g. `'de'`, `'fr'`) to its default ISO
 * locale (e.g. `'de-DE'`, `'fr-FR'`). Returns null for unknown subtags and for
 * full locale strings (which are not primary subtags).
 */
function expandPrimarySubtag(s) {
    return PRIMARY_SUBTAG_DEFAULT[s] ?? null;
}
/**
 * Extract the BCP 47 primary subtag (the language code without the region):
 * `'fr-CH'` → `'fr'`, `'en-US'` → `'en'`. Empty/null inputs return null.
 *
 * Canonical implementation — every place that needs the bare language code
 * should call this rather than re-implementing `split('-')[0]`.
 */
function localeToLanguageCode(locale) {
    if (!locale)
        return null;
    const subtag = locale.split('-')[0];
    return subtag || null;
}
//# sourceMappingURL=locale.js.map